import React, { useMemo } from "react";
import { ClassFilterOverlay } from "@components/Class/ClassFilter/ClassFilterOverlay";
import { BaseCellProps } from "@components/spreadsheet/cells/index";
import { useClasses } from "@api/class.api";
import { Class } from "@common/types/class";
import { ClassAvatarGroup } from "@components/avatar/ClassAvatarGroup";
import styles from "./ClassCell.module.scss";
import { stopPropagation, stopPropagationExceptTab } from "./cellEditorEvents";

export const ClassCell: React.FC<BaseCellProps<string[]>> = ({
    value,
    isEditing,
    onChange,
}) => {
    const { classesMap, sortedClassesIndexMap } = useClasses();

    const selectedClasses = useMemo(() => {
        if (!value) {
            return [];
        }

        const valueArray = Array.isArray(value) ? value : [value];

        const extractedClasses = valueArray
            .map((v) => classesMap.get(v))
            .filter(Boolean) as Class[];

        return extractedClasses.toSorted((a, b) => {
            const aIndex = sortedClassesIndexMap.get(a.id) ?? Infinity;
            const bIndex = sortedClassesIndexMap.get(b.id) ?? Infinity;
            return aIndex - bIndex;
        });
    }, [classesMap, value, sortedClassesIndexMap]);

    if (isEditing) {
        return (
            <div
                className={styles.classCell}
                onClick={stopPropagation}
                onMouseDown={stopPropagation}
                onKeyDown={stopPropagationExceptTab}
            >
                <ClassFilterOverlay
                    selectedClasses={value ?? []}
                    onChange={onChange}
                    behaviour={{
                        selectedParentClassIncludesAllChildren: false,
                        canSelectOnlyOneClassInTheSameRoot: true,
                    }}
                    useAutoHeight
                />
            </div>
        );
    }

    if (selectedClasses.length === 0) {
        return null;
    }

    return (
        <ClassAvatarGroup
            classes={selectedClasses}
            max={3}
            className={styles.classAvatarGroup}
        />
    );
};
